import { MutableRefObject } from 'react'
import dayjs from 'dayjs'

import { IGetUserListShort } from '../../../../types/teams'
import { IUserToImportData } from '../types'

type AddErrorFn = (fieldKey: string, error: { message: string; level: 'info' | 'warning' | 'error' }) => void

export interface IValidateImportUsersRowRefs {
  notImportedUsersRef: MutableRefObject<IUserToImportData[]>
  activeUsersRef: MutableRefObject<IGetUserListShort[]>
  departmentsRef: MutableRefObject<{ id: string; name: string }[]>
  locationsRef: MutableRefObject<{ id: string; name: string }[]>
  hasLoadedNotImportedUsersRef: MutableRefObject<boolean>
  formatMessage: (descriptor: { id: string }) => string
}

// Precomputed lookup structures to avoid O(N) scans and JSON.parse per row
interface ILookupCache {
  directoryEmails: Set<string>
  activeEmails: Set<string>
  departmentNames: Set<string>
  locationNames: Set<string>
  sourceNotImported: IUserToImportData[]
  sourceActive: IGetUserListShort[]
  sourceDepts: { id: string; name: string }[]
  sourceLocs: { id: string; name: string }[]
}

let lookupCache: ILookupCache | null = null

const buildLookupCache = (
  notImportedUsers: IUserToImportData[],
  activeUsers: IGetUserListShort[],
  departments: { id: string; name: string }[],
  locations: { id: string; name: string }[]
): ILookupCache => {
  // Only rebuild if sources changed
  if (
    lookupCache &&
    lookupCache.sourceNotImported === notImportedUsers &&
    lookupCache.sourceActive === activeUsers &&
    lookupCache.sourceDepts === departments &&
    lookupCache.sourceLocs === locations
  ) {
    return lookupCache
  }

  const directoryEmails = new Set<string>()
  for (const entry of notImportedUsers) {
    const parsed = JSON.parse(entry.value)
    const email = (parsed.mail || parsed.name || '').toLowerCase()
    if (email) directoryEmails.add(email)
  }

  const activeEmails = new Set<string>()
  for (const user of activeUsers) {
    if (user.email) activeEmails.add(user.email.toLowerCase())
  }

  const departmentNames = new Set<string>(departments.map((d) => d.name.toLowerCase()))
  const locationNames = new Set<string>(locations.map((l) => l.name.toLowerCase()))

  lookupCache = {
    directoryEmails,
    activeEmails,
    departmentNames,
    locationNames,
    sourceNotImported: notImportedUsers,
    sourceActive: activeUsers,
    sourceDepts: departments,
    sourceLocs: locations,
  }
  return lookupCache
}

export const validateImportUsersRow = ({
  notImportedUsersRef,
  activeUsersRef,
  departmentsRef,
  locationsRef,
  hasLoadedNotImportedUsersRef,
  formatMessage,
}: IValidateImportUsersRowRefs) => {
  return (data, addError: AddErrorFn) => {
    const email = (data.email as string)?.trim().toLowerCase()
    if (!email) return data

    const cache = buildLookupCache(notImportedUsersRef.current, activeUsersRef.current, departmentsRef.current, locationsRef.current)

    // Check if user is already imported in the system
    if (cache.activeEmails.has(email)) {
      addError('email', {
        message: formatMessage({ id: 'components.importUsersForm.csv.userAlreadyImported' }),
        level: 'error',
      })
      return data
    }

    // Check if user exists in platform directory (only when directory data is loaded)
    if (hasLoadedNotImportedUsersRef.current && !cache.directoryEmails.has(email)) {
      addError('email', {
        message: formatMessage({ id: 'components.importUsersForm.csv.userNotInDirectory' }),
        level: 'error',
      })
    }

    // Validate department name matches an existing department
    if (data.department && cache.departmentNames.size > 0) {
      if (!cache.departmentNames.has((data.department as string).trim().toLowerCase())) {
        addError('department', {
          message: formatMessage({ id: 'app.importLeaves.errors.departmentNotFound' }),
          level: 'error',
        })
      }
    }

    // Validate location name matches an existing location
    if (data.location && cache.locationNames.size > 0) {
      if (!cache.locationNames.has((data.location as string).trim().toLowerCase())) {
        addError('location', {
          message: formatMessage({ id: 'app.importLeaves.errors.locationNotFound' }),
          level: 'error',
        })
      }
    }

    if (data.birthday) {
      const birthday = dayjs(data.birthday as string)
      if (!birthday.isValid()) {
        addError('birthday', {
          message: formatMessage({ id: 'app.importLeaves.errors.birthdayNotValid' }),
          level: 'error',
        })
      } else {
        data.birthday = birthday.format('YYYY-MM-DD')
      }
    }

    return data
  }
}
